import React from "react";
import { toast } from "react-toastify";
import { friendlyError } from "../Policy/OnlinePolicy/utils/friendlyError";

/**
 * The creator's toasts for live policy updates (2026-10-22).
 *
 * A user who creates many policies at once gets their results at about the same time. Messages that arrive within
 * BURST_MS are held and shown together: up to SINGLE_MAX as one toast each (click = that policy), more than that as ONE
 * summary toast ("8 policies finished - 5 completed, 3 failed", click = the list). The app's ToastContainer limit (4)
 * and a toastId per policy+status are the last guards against floods and duplicates.
 *
 * The toast function, the clock and where a click goes are passed in, so policyToasts.test.js runs without timers.
 */
export const BURST_MS = 1500;
export const SINGLE_MAX = 3;
const AUTO_CLOSE_MS = 8000;

const TYPE_OF = { completed: "success", completed_with_errors: "warning", failed: "error" };

let buffer = [];
let timer = null;
let navigateTo = (path) => window.location.assign(path);

/** Where a toast click goes (the navbar hands in react-router's navigate, so no page reload). */
export const setToastNavigator = (fn) => {
  if (typeof fn === "function") navigateTo = fn;
};

const Body = ({ title, text }) => (
  <div style={{ cursor: "pointer" }}>
    <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>{title}</div>
    {text ? <div style={{ fontSize: 12.5, marginTop: 2, opacity: 0.92, lineHeight: 1.35 }}>{text}</div> : null}
    <div style={{ fontSize: 11.5, marginTop: 4, opacity: 0.8 }}>Click to open</div>
  </div>
);

/** Counts for the summary toast. */
export const countTones = (list) =>
  list.reduce(
    (acc, m) => {
      const tone = m.tone || "failed";
      acc[tone] = (acc[tone] || 0) + 1;
      return acc;
    },
    { completed: 0, completed_with_errors: 0, failed: 0 }
  );

export const summaryText = (list) => {
  const c = countTones(list);
  const bits = [];
  if (c.completed) bits.push(`${c.completed} completed`);
  if (c.completed_with_errors) bits.push(`${c.completed_with_errors} completed with errors`);
  if (c.failed) bits.push(`${c.failed} failed`);
  return bits.join(", ");
};

/** Show what is held: one toast each, or one summary for a big burst. */
export function flushPolicyToasts({ show = toast } = {}) {
  const list = buffer;
  buffer = [];
  if (timer) clearTimeout(timer);
  timer = null;
  if (!list.length) return;

  if (list.length <= SINGLE_MAX) {
    for (const m of list) {
      // a failure's reason in plain words, like everywhere else (utils/friendlyError.js)
      const why = m.reason ? friendlyError(m.reason).title : "";
      show(<Body title={m.title} text={[m.text, why].filter(Boolean).join(" - ")} />, {
        type: TYPE_OF[m.tone] || "info",
        toastId: `policy-${m.policyId}-${m.status}`,
        autoClose: AUTO_CLOSE_MS,
        position: "top-right",
        theme: "colored",
        closeOnClick: true,
        pauseOnHover: true,
        onClick: () => navigateTo(`/onlinePolicy/view/form/${m.policyId}`),
      });
    }
    return;
  }

  const c = countTones(list);
  show(<Body title={`${list.length} policies finished`} text={summaryText(list)} />, {
    type: c.failed || c.completed_with_errors ? "warning" : "success",
    toastId: `policy-burst-${list.map((m) => `${m.policyId}:${m.status}`).sort().join(",")}`,
    autoClose: AUTO_CLOSE_MS + 2000,
    position: "top-right",
    theme: "colored",
    closeOnClick: true,
    pauseOnHover: true,
    onClick: () => navigateTo("/onlinePolicy"),
  });
}

/** Hold a "job finished" message for the burst window. A repeat of the same policy+status is dropped. */
export function queuePolicyToast(message, { show = toast, setTimer = setTimeout } = {}) {
  if (!message || !message.policyId) return;
  if (buffer.some((m) => m.policyId === message.policyId && m.status === message.status)) return;
  buffer.push(message);
  if (!timer) timer = setTimer(() => flushPolicyToasts({ show }), BURST_MS);
}

/** Test seam. */
export const resetPolicyToastsForTest = () => {
  buffer = [];
  if (timer) clearTimeout(timer);
  timer = null;
};
